/**
 * The user's library of custom icons (uploaded SVG/PNG or pasted image URLs), so
 * they can be reused across nodes/diagrams without re-uploading.
 *
 * v1 is localStorage-backed (per-device). The interface is intentionally ASYNC and
 * the record shape mirrors a DB row, so moving to a synced, per-account table later
 * is a drop-in: reimplement these three functions against `/api/icons` endpoints and
 * NOTHING in the UI changes.
 */

export interface CustomIcon {
  /** Stable id — the R2 key/image URL doubles as the id in v1; a DB row id in v2. */
  id: string;
  /** The icon reference stored on a node: an R2 object key (uploads), or an
   *  external image URL pasted by the user. Resolved at render time by
   *  `resolveAssetUrl` (legacy absolute URLs from older domains included). */
  url: string;
  /** Display name shown in the picker. */
  name: string;
  /** Epoch ms; newest first in the picker. */
  createdAt: number;
}

const STORAGE_KEY = 'ss:custom-icons';
const MAX_ICONS = 60;

function read(): CustomIcon[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CustomIcon[]) : [];
  } catch {
    return [];
  }
}

function write(icons: CustomIcon[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(icons.slice(0, MAX_ICONS)));
  } catch {
    /* ignore quota / disabled storage */
  }
}

/** List the user's custom icons, newest first. */
export async function listCustomIcons(): Promise<CustomIcon[]> {
  return read().sort((a, b) => b.createdAt - a.createdAt);
}

/**
 * Add a custom icon to the library. Deduped by URL — because the backend uses
 * content-addressed filenames, identical uploads share a URL and collapse to one
 * library entry. Returns the (existing or new) record.
 */
export async function addCustomIcon(input: { url: string; name?: string }): Promise<CustomIcon> {
  const url = input.url.trim();
  const icons = read();
  const existing = icons.find((i) => i.url === url);
  if (existing) return existing;

  const record: CustomIcon = {
    id: url,
    url,
    name: input.name?.trim() || 'Custom icon',
    // localStorage v1 can't use a server clock; Date is fine in the browser.
    createdAt: Date.now(),
  };
  write([record, ...icons]);
  return record;
}

/** Remove a custom icon from the library (does not delete the R2 object). */
export async function removeCustomIcon(id: string): Promise<void> {
  write(read().filter((i) => i.id !== id));
}
